/**
 * Estimates List Page - T180
 *
 * List and filter cost estimates
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import {
  Button,
  EmptyState,
  FilterPill,
  GlassPanel,
  LoadingSpinner,
  PageHeader,
  Pagination,
  SearchInput,
} from '../components/ui';
import { EstimateCard } from '../components/estimates/EstimateCard';
import { useEstimates, statusLabels, type EstimateStatus } from '../hooks/useEstimates';

const estimatesSearchSchema = z.object({
  page: z.coerce.number().int().positive().optional().default(1),
  q: z.string().optional(),
  status: z.enum(['draft', 'sent', 'accepted', 'rejected', 'expired', 'converted']).optional(),
});

type EstimatesSearch = z.infer<typeof estimatesSearchSchema>;

export const Route = createFileRoute('/estimates')({
  component: EstimatesPage,
  validateSearch: estimatesSearchSchema,
});

const statusOptions: (EstimateStatus | 'all')[] = [
  'all',
  'draft',
  'sent',
  'accepted',
  'rejected',
  'expired',
  'converted',
];

function EstimatesPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const updateSearch = (patch: Partial<EstimatesSearch>) => {
    navigate({
      search: (previous: EstimatesSearch) => ({ ...previous, ...patch }),
      replace: true,
    });
  };

  const { data, isLoading } = useEstimates({
    page: search.page,
    limit: 40,
    status: search.status,
    search: search.q || undefined,
    sortBy: 'issueDate',
    sortOrder: 'desc',
  });

  const estimates = data?.estimates || [];
  const pagination = {
    page: data?.page || 1,
    totalPages: data?.totalPages || 1,
  };

  return (
    <ProtectedRoute>
      <AppShell>
        <PageHeader
          title="Angebote"
          subtitle={`${data?.total || 0} Angebot${data?.total !== 1 ? 'e' : ''}`}
          actions={
            <Link to="/estimates/new">
              <Button>+ Neues Angebot</Button>
            </Link>
          }
        />

        {/* Filters — Sprint 006 A3: Suche links, Status-Pills rechts (Kanon,
            war zuvor spiegelverkehrt zu /contracts) */}
        <GlassPanel className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            <div className="flex-1">
              <SearchInput
                placeholder="Suchen nach Nummer, Betreff…"
                value={search.q ?? ''}
                onChange={(q) => updateSearch({ q: q || undefined, page: 1 })}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((s) => (
                <FilterPill
                  key={s}
                  active={(s === 'all' && !search.status) || s === search.status}
                  onClick={() => updateSearch({ status: s === 'all' ? undefined : s, page: 1 })}
                >
                  {s === 'all' ? 'Alle' : statusLabels[s]}
                </FilterPill>
              ))}
            </div>
          </div>
        </GlassPanel>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : estimates.length === 0 ? (
          <EmptyState
            title="Keine Angebote gefunden"
            description={
              search.q || search.status
                ? 'Versuchen Sie, Ihre Filter anzupassen'
                : 'Erstellen Sie Ihr erstes Angebot'
            }
            action={
              !search.q && !search.status
                ? { label: 'Neues Angebot', onClick: () => navigate({ to: '/estimates/new' }) }
                : undefined
            }
          />
        ) : (
          <>
            {/* Grid */}
            <GlassPanel
              variant="subtle"
              padding="sm"
              className="overflow-hidden mb-6"
              data-testid="estimates-list-surface"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {estimates.map((estimate) => (
                  <EstimateCard key={estimate.id} estimate={estimate} />
                ))}
              </div>
            </GlassPanel>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Pagination
                page={search.page}
                totalPages={pagination.totalPages}
                onPageChange={(page) => updateSearch({ page })}
              />
            )}
          </>
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
